import React, { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Gauge,
  Buildings,
  Receipt,
  ListChecks,
  Pulse,
  ShieldStar,
  SignOut,
  User as UserIcon,
  List,
  PushPinSimple,
  SidebarSimple,
} from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const BRAND_LOGO = process.env.PUBLIC_URL + "/icone-logo-up2.png";
const DESKTOP_NAV_MODE_KEY = "xmlmonitor.desktopNavMode";

const getInitialDesktopNavMode = () => {
  if (typeof window === "undefined") return "fixed";
  try {
    return localStorage.getItem(DESKTOP_NAV_MODE_KEY) === "drawer"
      ? "drawer"
      : "fixed";
  } catch {
    return "fixed";
  }
};

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [desktopNavMode, setDesktopNavMode] = useState(getInitialDesktopNavMode);
  const isDesktopDrawerMode = desktopNavMode === "drawer";

  useEffect(() => {
    try {
      localStorage.setItem(DESKTOP_NAV_MODE_KEY, desktopNavMode);
    } catch {
      /* ignore */
    }
  }, [desktopNavMode]);

  const useDrawerMenu = () => {
    setDesktopNavMode("drawer");
    setOpen(false);
  };

  const useFixedMenu = () => {
    setDesktopNavMode("fixed");
    setOpen(false);
  };

  const navItems = [
    { to: "/", label: "Dashboard", icon: Gauge, end: true },
    { to: "/tenants", label: "Empresas / CNPJs", icon: Buildings },
    { to: "/documentos", label: "Documentos NF-e", icon: Receipt },
    { to: "/eventos", label: "Eventos", icon: ListChecks },
    { to: "/logs", label: "Logs do Worker", icon: Pulse },
  ];

  const adminItems = [
    { to: "/admin", label: "Administração", icon: ShieldStar },
  ];

  // Componente de navegação para evitar repetição
  const NavigationContent = ({ compactActions = false } = {}) => (
    <div className="flex flex-col h-full bg-white">
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src={BRAND_LOGO}
              alt="UP2 Soluções"
              className="h-10 w-10 object-contain rounded-sm bg-muted/40 p-0.5"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold tracking-tight leading-tight truncate">
                UP2 Soluções
              </div>
              <div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground truncate">
                SEFAZ · NF-e / NFC-e
              </div>
            </div>
          </div>
          {!compactActions && (
            <button
              type="button"
              onClick={useDrawerMenu}
              title="Usar menu em gaveta"
              aria-label="Usar menu em gaveta"
              className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-border text-muted-foreground hover:bg-muted hover:text-foreground md:inline-flex"
            >
              <SidebarSimple size={18} />
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-thin">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)} // Fecha o menu ao clicar (importante no mobile)
            className={({ isActive }) =>
              [
                "flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-muted",
              ].join(" ")
            }
          >
            <Icon size={18} weight="regular" />
            <span>{label}</span>
          </NavLink>
        ))}
        {isAdmin && (
          <>
            <div className="label-overline pt-5 pb-1.5 px-3">Administração</div>
            {adminItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted",
                  ].join(" ")
                }
              >
                <Icon size={18} weight="regular" />
                <span>{label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="border-t border-border p-3">
        <div className="px-2 py-2 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
            <UserIcon size={14} weight="regular" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">
              {user?.email}
            </div>
          </div>
          <button
            onClick={logout}
            title="Sair"
            className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            <SignOut size={16} />
          </button>
        </div>
        <div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground mt-1 px-2">
          {user?.role === "platform_admin"
            ? "Administrador"
            : user?.group?.nome
              ? `grupo · ${user.group.nome}`
              : "sem grupo"}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={
        isDesktopDrawerMode
          ? "min-h-screen flex flex-col bg-background"
          : "min-h-screen flex flex-col md:flex-row bg-background"
      }
    >
      {/* Header mobile e topo do modo gaveta no desktop */}
      <header
        className={[
          "flex items-center justify-between px-4 py-3 bg-white border-b border-border sticky top-0 z-50",
          isDesktopDrawerMode ? "" : "md:hidden",
        ].join(" ")}
      >
        <div className="flex items-center gap-2">
          <img src={BRAND_LOGO} alt="Logo" className="h-8 w-8 object-contain" />
          <span className="font-semibold text-sm tracking-tight">
            UP2 Soluções
          </span>
        </div>
        <div className="flex items-center gap-1">
          {isDesktopDrawerMode && (
            <Button
              variant="ghost"
              size="icon"
              title="Fixar menu lateral"
              aria-label="Fixar menu lateral"
              onClick={useFixedMenu}
              className="hidden h-9 w-9 md:inline-flex"
            >
              <PushPinSimple size={20} />
            </Button>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                title="Abrir menu"
                aria-label="Abrir menu"
                className="h-9 w-9"
              >
                <List size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <NavigationContent compactActions />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Sidebar fixa do desktop */}
      {!isDesktopDrawerMode && (
        <aside className="hidden md:flex w-64 shrink-0 border-r border-border bg-white flex-col sticky top-0 h-screen">
          <NavigationContent />
        </aside>
      )}

      {/* Conteúdo Principal */}
      <main className="flex-1 overflow-auto scrollbar-thin flex flex-col min-w-0">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
